import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NzCardComponent} from "ng-zorro-antd/card";
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from "@angular/forms";
import {NzFormControlComponent, NzFormItemComponent, NzFormLabelComponent} from "ng-zorro-antd/form";
import {
  NzInputDirective, NzInputPasswordDirective,
  NzInputPrefixDirective,
  NzInputWrapperComponent
} from "ng-zorro-antd/input";
import {NzIconDirective} from "ng-zorro-antd/icon";
import {AuthenticateResponse, AuthFlowService} from "../../core/services/auth-flow.service";
import {NzButtonComponent} from "ng-zorro-antd/button";
import {NzAlertComponent} from "ng-zorro-antd/alert";
import {NzStepComponent, NzStepsComponent} from "ng-zorro-antd/steps";
import {NzModalModule, NzModalRef, NzModalService} from "ng-zorro-antd/modal";
import {ForgotPasswordModal, ForgotPasswordModalData} from "./forgot-password-modal/forgot-password-modal";
import {SetPasswordWithXmlModal} from "./set-password-with-xml-modal/set-password-with-xml-modal";
import {ExpiredPsdModal, ExpiredPsdModalData} from "./expired-psd-modal/expired-psd-modal";
import {ConsentModal} from "./consent-modal/consent-modal";
import {finalize, map, switchMap} from "rxjs";
import {NcalayerService} from "../../core/services/ncalayer.service";
import {NcalayerCancelledError} from "../../core/services/ncalayer-cancelled-error";
import {NcalayerUnavailableError} from "../../core/services/ncalayer-unavailable-error";
import { v4 as uuidv4 } from 'uuid';
import {NzNotificationService} from "ng-zorro-antd/notification";
import {AuthApiService} from "../../core/services/auth-api.service";
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {textControl} from '../../shared/text-control';
import {environment} from '../../../environments/environment';

@Component({
  selector: 'app-login',
  imports: [
    NzCardComponent,
    ReactiveFormsModule,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzFormControlComponent,
    NzInputDirective,
    NzInputWrapperComponent,
    NzIconDirective,
    NzInputPrefixDirective,
    NzInputPasswordDirective,
    NzButtonComponent,
    NzAlertComponent,
    NzStepsComponent,
    NzStepComponent,
    NzModalModule,
    RouterLink,
    TranslatePipe,
  ],
  templateUrl: './login.html',
  styleUrl: './login.css',
})
export class Login implements OnInit {
  private static safeLocalUrl(url: string | null | undefined): string | null {
    return url && /^\/(?![\/\\])/.test(url) ? url : null;
  }

  private launchOrigin(): string | null {
    const explicit = this._route.snapshot.queryParamMap.get('origin');
    if (explicit) return explicit;

    const returnUrl = this._route.snapshot.queryParamMap.get('ReturnUrl');
    const candidate = this._route.snapshot.queryParamMap.get('redirect_uri')
      ?? this._route.snapshot.queryParamMap.get('returnUrl')
      ?? (returnUrl ? new URLSearchParams(returnUrl.slice(returnUrl.indexOf('?') + 1)).get('redirect_uri') : null);

    if (!candidate) return null;

    try {
      return new URL(candidate, window.location.origin).origin;
    } catch {
      return null;
    }
  }


  private readonly _authFlowService = inject(AuthFlowService);
  private readonly _ncalayerSerivce = inject(NcalayerService);
  private readonly _notification = inject(NzNotificationService);
  private readonly _authApiService = inject(AuthApiService);
  protected readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _modal = inject(NzModalService);
  private readonly _translate = inject(TranslateService);

  protected get lawLinks() {
    const lang = (this._translate.currentLang() ?? '').toLowerCase() === 'kz' ? 'kz' : 'ru';
    return environment.lawLinks[lang];
  }

  private _forgotModalRef: NzModalRef | null = null;
  private _isOpeningForgot = false;

  visible = false;
  currentStep = 0;
  isSigning = false;
  isAuthenticating = false;
  private certLogin: string | null = null;

  form: FormGroup = new FormGroup({
    login: new FormControl(null, [
      Validators.required,
      Validators.minLength(12),
      Validators.maxLength(12)
    ]),
    password: textControl([Validators.required]),
    xml: new FormControl(null, Validators.required)
  });

  ngOnInit(): void {
    this.form.get('login')?.disable();

    const hasClientId = this._route.snapshot.queryParamMap.has('client_id');
    const origin = this._route.snapshot.queryParamMap.get('origin');
    const oauthReturnUrl = this._route.snapshot.queryParamMap.get('ReturnUrl');

    if (!hasClientId && !origin && !oauthReturnUrl) {
      return;
    }

    this._authApiService.loadSession().subscribe((authed) => {
      if (!authed) return;

      const safeReturn = Login.safeLocalUrl(oauthReturnUrl);
      if (safeReturn) {
        window.location.href = safeReturn;
        return;
      }

      this._authFlowService.availableSystems().subscribe((systems) => {
        if (hasClientId && this.tryContinueOAuthFlow({systems})) {
          return;
        }
        const launchOrigin = this.launchOrigin();
        if (launchOrigin) {
          this._router.navigate(['/main'], { state: { systems }, queryParams: { origin: launchOrigin } });
        }
      });
    });
  }

  tryAuthenticate(): void {
    this.isAuthenticating = true;

    this._authFlowService.authenticate({
      psd: this.form.get('password')?.value,
      xml: this.form.get('xml')?.value
    }).pipe(
      finalize(() => this.isAuthenticating = false),
    ).subscribe((res) => {
      if (res.psdExpired) {
        this.openExpiredPsdModal(res.psdResetToken, res.psdTemporary === true);
        return;
      }

      if (res.consentRequired) {
        this.openConsentModal(res.consentToken);
        return;
      }

      this._authApiService.refreshSession().subscribe(() => {
        const safeReturn = Login.safeLocalUrl(this._route.snapshot.queryParamMap.get('ReturnUrl'));
        if (safeReturn) {
          window.location.href = safeReturn;
          return;
        }

        if (this.tryContinueOAuthFlow(res)) {
          return;
        }

        const redirectUri = this._route.snapshot.queryParamMap.get('redirect_uri')
          ?? this._route.snapshot.queryParamMap.get('returnUrl');
        const origin = this.launchOrigin();

        const queryParams: Record<string, string> = {};
        if (redirectUri) queryParams['redirect_uri'] = redirectUri;
        if (origin) queryParams['origin'] = origin;

        this._router.navigate(['/main'], {
          state: { systems: res.systems },
          queryParams: Object.keys(queryParams).length ? queryParams : undefined
        });
      });
    });
  }

  signWithCertificate(): void {
    this.isSigning = true;

    this._ncalayerSerivce.signXml(this.generateXml(), true)
      .pipe(
        switchMap((signedXml) =>
          this._authFlowService.verifyEcpXml({xml: signedXml})
            .pipe(map((person) => ({signedXml, person})))
        ),
        finalize(() => (this.isSigning = false))
      )
      .subscribe({
        next: ({signedXml, person}) => {
          this.certLogin = person.iin || null;
          if (!this.certLogin) {
            this._notification.error(
              this._translate.instant('common.error'),
              this._translate.instant('login.iinNotFromCert')
            );
            return;
          }

          this.form.get('xml')?.setValue(signedXml);
          this.form.patchValue({login: this.certLogin});
          this.currentStep = 1;
        },
        error: (error) => {
          if (error instanceof NcalayerCancelledError) {
            this._notification.warning(
              this._translate.instant('ncalayer.cancelledTitle'),
              this._translate.instant('ncalayer.cancelledDesc')
            );
            return;
          }

          if (error instanceof NcalayerUnavailableError) {
            this._notification.warning(
              this._translate.instant('ncalayer.unavailableTitle'),
              this._translate.instant('ncalayer.unavailableDesc')
            );
            return;
          }

          console.error('Ошибка подписи через NCALayer:', error);
        }
      });
  }

  backToSignStep(): void {
    this.currentStep = 0;
    this.form.reset();
  }

  openForgotPassword(): void {
    if (this._forgotModalRef || this._isOpeningForgot) {
      return;
    }

    if (!this.form.get('xml')?.value) {
      this._notification.error(this._translate.instant('common.error'), this._translate.instant('login.signFirst'));
      return;
    }

    this._modal.create({
      nzTitle: this._translate.instant('forgotPsd.title'),
      nzContent: this._translate.instant('forgotPsd.confirmContent'),
      nzClosable: false,
      nzMaskClosable: false,
      nzOkText: this._translate.instant('forgotPsd.send'),
      nzCancelText: this._translate.instant('common.cancel'),
      nzOnOk: () => this.sendForgotLink(),
    });
  }

  private sendForgotLink(): void {
    const xml = this.form.get('xml')?.value;
    if (!xml) {
      return;
    }

    this._isOpeningForgot = true;
    this._authFlowService.forgotPsd({xml})
      .pipe(
        finalize(() => this._isOpeningForgot = false),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (res) => {
          if (res.requiresEmailSetup) {
            this.openForgotModal({xml});
          } else {
            this._notification.success(
              this._translate.instant('forgotPsd.linkSentTitle'),
              this._translate.instant('forgotPsd.linkSentDesc')
            );
          }
        },
        error: () => {},
      });
  }

  private openForgotModal(data: ForgotPasswordModalData): void {
    const modalRef = this._modal.create({
      nzTitle: this._translate.instant('forgotPsd.title'),
      nzContent: ForgotPasswordModal,
      nzData: data,
      nzOkText: this._translate.instant('forgotPsd.send'),
      nzCancelText: this._translate.instant('common.cancel'),
      nzOkDisabled: true,
      nzOnOk: (instance) => {
        instance.handleOk();
        return false;
      },
    });

    this._forgotModalRef = modalRef;
    modalRef.afterClose
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this._forgotModalRef = null);
  }

  private openExpiredPsdModal(token: string | undefined, temporary: boolean): void {
    if (!token) {
      this._notification.error(
        this._translate.instant('expiredPsd.errorTitle'),
        this._translate.instant('expiredPsd.noToken')
      );
      return;
    }

    const modalRef = this._modal.create<ExpiredPsdModal, ExpiredPsdModalData>({
      nzTitle: this._translate.instant(temporary ? 'expiredPsd.tempTitle' : 'expiredPsd.title'),
      nzContent: ExpiredPsdModal,
      nzData: {token, temporary},
      nzOkText: this._translate.instant('expiredPsd.submit'),
      nzCancelText: this._translate.instant('common.cancel'),
      nzOkDisabled: true,
      nzMaskClosable: false,
      nzOnOk: (instance) => {
        instance.handleOk();
        return false;
      },
    });

    modalRef.afterClose
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((newPsd?: string) => {
        if (!newPsd) {
          return;
        }

        this.form.get('password')?.setValue(newPsd);
        this.tryAuthenticate();
      });
  }

  private openConsentModal(consentToken?: string): void {
    const modalRef = this._modal.create<ConsentModal, {consentToken?: string}>({
      nzTitle: this._translate.instant('consent.title'),
      nzContent: ConsentModal,
      nzData: {consentToken},
      nzOkText: this._translate.instant('consent.submit'),
      nzCancelText: this._translate.instant('common.cancel'),
      nzOkDisabled: true,
      nzMaskClosable: false,
      nzWidth: 960,
      nzOnOk: (instance) => {
        instance.handleOk();
        return false;
      },
    });

    modalRef.afterClose
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((accepted?: boolean) => {
        if (accepted) {
          this.tryAuthenticate();
        }
      });
  }

  openChangePasswordModal(): void {
    const xml = this.form.get('xml')?.value;
    if (!xml) {
      this._notification.error(this._translate.instant('common.error'), this._translate.instant('login.signFirst'));
      return;
    }

    this._modal.create({
      nzTitle: this._translate.instant('changePsd.title'),
      nzContent: SetPasswordWithXmlModal,
      nzData: {xml},
      nzOkText: this._translate.instant('changePsd.submit'),
      nzCancelText: this._translate.instant('common.cancel'),
      nzOkDisabled: true,
      nzOnOk: (instance) => {
        instance.handleOk();
        return false;
      },
    });
  }

  private generateXml(): string {
    return `<xml><documentType>Authentication</documentType><ticketTime>${Date.now().toString()}</ticketTime><id>${uuidv4()}</id></xml>`;
  }

  private tryContinueOAuthFlow(res: AuthenticateResponse): boolean {
    const clientId = this._route.snapshot.queryParamMap.get('client_id');
    const redirectUri = this._route.snapshot.queryParamMap.get('redirect_uri')
      ?? this._route.snapshot.queryParamMap.get('returnUrl');

    if (!clientId || !redirectUri) {
      return false;
    }

    const system = res.systems.find((item) =>
      item.audience?.toLowerCase() === clientId.toLowerCase() ||
      item.clientId?.toLowerCase() === clientId.toLowerCase());

    if (!system) {
      this._notification.error(
        this._translate.instant('common.error'),
        this._translate.instant('login.oauthModuleNotFound')
      );
      return true;
    }

    const params = new URLSearchParams();
    params.set('response_type', 'code');
    params.set('client_id', clientId);
    params.set('redirect_uri', redirectUri);
    params.set('scope', this._route.snapshot.queryParamMap.get('scope') ?? 'openid profile offline_access');

    const state = this._route.snapshot.queryParamMap.get('state');
    if (state) params.set('state', state);
    const nonce = this._route.snapshot.queryParamMap.get('nonce');
    if (nonce) params.set('nonce', nonce);
    const codeChallenge = this._route.snapshot.queryParamMap.get('code_challenge');
    if (codeChallenge) {
      params.set('code_challenge', codeChallenge);
      params.set('code_challenge_method', this._route.snapshot.queryParamMap.get('code_challenge_method') ?? 'S256');
    }

    window.location.href = `/api/sso/connect/authorize?${params.toString()}`;
    return true;
  }
}
